import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from 'lucide-react';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
}

const PRESETS = [
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last year', days: 365 },
  { label: 'All time', days: 0 },
] as const;

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getPresetRange(days: number): { start: string; end: string } {
  const end = formatDate(new Date());
  if (days === 0) {
    return { start: '', end: '' };
  }
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  return { start: formatDate(startDate), end };
}

function getActivePreset(startDate: string, endDate: string): number | null {
  if (!startDate && !endDate) return 0; // All time
  for (const preset of PRESETS) {
    if (preset.days === 0) continue;
    const range = getPresetRange(preset.days);
    if (range.start === startDate && range.end === endDate) {
      return preset.days;
    }
  }
  return null;
}

export function DateRangePicker({ startDate, endDate, onChange }: DateRangePickerProps) {
  const activePreset = getActivePreset(startDate, endDate);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Calendar className="h-4 w-4 text-text-tertiary" />
      {PRESETS.map((preset) => (
        <Button
          key={preset.days}
          variant={activePreset === preset.days ? 'default' : 'secondary'}
          size="sm"
          onClick={() => {
            const range = getPresetRange(preset.days);
            onChange(range.start, range.end);
          }}
        >
          {preset.label}
        </Button>
      ))}
      <div className="flex items-center gap-2 ml-2">
        <Input
          type="date"
          value={startDate}
          onChange={(e) => onChange(e.target.value, endDate)}
          className="w-36 h-9 text-xs"
          placeholder="Start date"
        />
        <span className="text-text-tertiary text-sm">to</span>
        <Input
          type="date"
          value={endDate}
          onChange={(e) => onChange(startDate, e.target.value)}
          className="w-36 h-9 text-xs"
          placeholder="End date"
        />
      </div>
    </div>
  );
}
